// src/pages/HangXuatPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import {
  collection,
  addDoc,
  doc,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import {
  Box, Typography, Button, Paper, Table, TableContainer, TableHead, TableRow, TableCell,
  TableBody, IconButton, TextField, TablePagination, Chip, Grid, Stack, Tooltip, Menu, MenuItem,
  FormControl, InputLabel, Select
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AllInboxIcon from '@mui/icons-material/AllInbox';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PrintIcon from '@mui/icons-material/Print';

import { useAuth } from '../context/AuthContext';
import { useAppContext } from '../context/AppContext';
import ExportDialog from '../components/ExportDialog';
import CustomerDialog from '../components/CustomerDialog';
import SimpleEntityDialog from '../components/SimpleEntityDialog';
import PrintDialog from '../components/PrintDialog';
import { startOfMonth, endOfMonth, subMonths } from 'date-fns';
import * as XLSX from 'xlsx';
import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';
import { saveAs } from 'file-saver';

/* ---------------- Helpers ---------------- */
const toJSDate = (v) => {
  if (!v) return null;
  try { return typeof v?.toDate === 'function' ? v.toDate() : new Date(v); } catch { return null; }
};
const formatDateTime = (ts) => {
  const d = toJSDate(ts);
  return d ? d.toLocaleString('vi-VN') : 'N/A';
};
const formatCurrency = (n) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' })
    .format(Number(n || 0));

function descendingComparator(a, b, orderBy) {
  const av = a?.[orderBy];
  const bv = b?.[orderBy];
  const ax = typeof av?.toDate === 'function' ? av.toDate().getTime() : (av?.seconds ? av.seconds * 1000 : av);
  const bx = typeof bv?.toDate === 'function' ? bv.toDate().getTime() : (bv?.seconds ? bv.seconds * 1000 : bv);
  if ((bx ?? 0) < (ax ?? 0)) return -1;
  if ((bx ?? 0) > (ax ?? 0)) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}
const getOrderStatusChipProps = (status) => {
  const s = status || 'processing';
  switch (s) {
    case 'shipped': return { label: 'Đã chuyển', color: 'success' };
    case 'cancelled': return { label: 'Đã hủy', color: 'error' };
    case 'processing': return { label: 'Đang xử lý', color: 'info' };
    default: return { label: s, color: 'default' };
  }
};

const StatCard = ({ title, value, icon, color = 'text.primary', percentageChange }) => {
  const isPositive = (percentageChange ?? 0) >= 0;
  return (
    <Paper elevation={3} sx={{ p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
        <Box sx={{ color }}>{icon}</Box>
      </Box>
      <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', my: 1 }}>{value}</Typography>
      {Number.isFinite(percentageChange) && (
        <Stack direction="row" alignItems="center" spacing={0.5}
               sx={{ color: isPositive ? 'success.main' : 'error.main', alignSelf: 'flex-start' }}>
          {isPositive ? <ArrowUpwardIcon fontSize="inherit" /> : <ArrowDownwardIcon fontSize="inherit" />}
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
            {Math.abs(percentageChange).toFixed(1)}% so với tháng trước
          </Typography>
        </Stack>
      )}
    </Paper>
  );
};

const getCustomerDisplayName = (c) =>
  c?.name || c?.customerName || c?.fullName || c?.title || c?.displayName || '';

/** Preset range helpers (YYYY-MM-DD strings) */
const toYMD = (d) => d.toISOString().slice(0, 10);
const presetRange = (key) => {
  const t = new Date();
  if (key === 'today') {
    return { from: toYMD(t), to: toYMD(t) };
  }
  if (key === '7weeks') {
    const from = new Date(t);
    // 7 tuần qua = 49 ngày (tính cả hôm nay): lùi 48 ngày
    from.setDate(t.getDate() - 48);
    return { from: toYMD(from), to: toYMD(t) };
  }
  if (key === 'thisMonth') {
    const from = new Date(t.getFullYear(), t.getMonth(), 1);
    const to = new Date(t.getFullYear(), t.getMonth() + 1, 0);
    return { from: toYMD(from), to: toYMD(to) };
  }
  return { from: '', to: '' };
};

/* ---------------- Page ---------------- */
function HangXuatPage() {
  const {
    exports, products, customers, drivers,
    assistantDrivers, vehicles, warehouses
  } = useAppContext();
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [currentExport, setCurrentExport] = useState(null);
  const [dialogs, setDialogs] = useState({
    export: false, customer: false, driver: false, assistantDriver: false, vehicle: false, warehouse: false, print: false
  });
  const [orderToPrint, setOrderToPrint] = useState(null);

  // --- Bộ lọc ---
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState([]); // array<string>
  const [selectedProducts, setSelectedProducts] = useState([]);   // array<string>
  // khoảng ngày lưu ở 2 state, điều khiển bởi preset dropdown
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [datePreset, setDatePreset] = useState(''); // '', 'today', '7weeks', 'thisMonth'

  // --- Sắp xếp, trang ---
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('exportDate');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (exports && products && customers) setLoading(false);
  }, [exports, products, customers]);

  // Áp preset ⇒ set from/to (inclusive)
  useEffect(() => {
    const { from, to } = presetRange(datePreset);
    setFromDate(from);
    setToDate(to);
  }, [datePreset]);

  /* ------------- Dashboard stats ------------- */
  const dashboardStats = useMemo(() => {
    const now = new Date();
    const currentMonthStart = startOfMonth(now);
    const prevMonthStart = startOfMonth(subMonths(now, 1));
    const prevMonthEnd = endOfMonth(subMonths(now, 1));

    const currentMonthExports = exports.filter(e => {
      const d = toJSDate(e.exportDate);
      return d && d >= currentMonthStart;
    });
    const prevMonthExports = exports.filter(e => {
      const d = toJSDate(e.exportDate);
      return d && d >= prevMonthStart && d <= prevMonthEnd;
    });

    const calc = (orders) => {
      const m = {
        totalRevenue: 0,
        orderCount: orders.length,
        status: { processing: 0, shipped: 0, cancelled: 0, other: 0 },
        payment: { paid: 0, partial: 0, unpaid: 0 },
      };
      orders.forEach(o => {
        m.totalRevenue += Number(o.grandTotal || 0);
        const k = o.status && m.status.hasOwnProperty(o.status) ? o.status : 'processing';
        m.status[k]++;
        if ((o.remainingAmount || 0) <= 0) m.payment.paid++;
        else if ((o.remainingAmount || 0) < (o.grandTotal || 0)) m.payment.partial++;
        else m.payment.unpaid++;
      });
      return m;
    };

    const cur = calc(currentMonthExports);
    const prv = calc(prevMonthExports);
    const pct = (c, p) => (p === 0 ? (c > 0 ? Infinity : 0) : ((c - p) / p) * 100);

    const productSales = exports.reduce((acc, e) => {
      (e.items || []).forEach(it => {
        acc[it.productId] = (acc[it.productId] || 0) + Number(it.quantity || 0);
      });
      return acc;
    }, {});
    const topSelling = Object.entries(productSales)
      .map(([id, qty]) => ({ name: products.find(p => p.id === id)?.name || 'Sản phẩm', quantitySold: qty }))
      .sort((a, b) => b.quantitySold - a.quantitySold);

    return {
      revenue: { value: formatCurrency(cur.totalRevenue), change: pct(cur.totalRevenue, prv.totalRevenue) },
      orders: { value: cur.orderCount, change: pct(cur.orderCount, prv.orderCount) },
      processing: { value: cur.status.processing, change: pct(cur.status.processing, prv.status.processing) },
      shipped: { value: cur.status.shipped, change: pct(cur.status.shipped, prv.status.shipped) },
      unpaid: { value: cur.payment.unpaid, change: pct(cur.payment.unpaid, prv.payment.unpaid) },
      partial: { value: cur.payment.partial, change: pct(cur.payment.partial, prv.payment.partial) },
      cancelled: { value: cur.status.cancelled, change: pct(cur.status.cancelled, prv.status.cancelled) },
      other: { value: cur.status.other, change: pct(cur.status.other, prv.status.other) },
      topSellingProduct: topSelling[0]?.name || 'N/A',
    };
  }, [exports, products]);

  /* ------------- Dropdown options ------------- */
  const customerOptions = useMemo(() => {
    const fromCustomers = Array.from(new Set((customers || []).map(getCustomerDisplayName).filter(Boolean)));
    const fromExports = Array.from(new Set((exports || []).map(e => (e.customerName || '').trim()).filter(Boolean)));
    return Array.from(new Set([...fromCustomers, ...fromExports])).sort((a, b) => a.localeCompare(b, 'vi'));
  }, [customers, exports]);

  const productOptions = useMemo(() => {
    const fromProducts = Array.from(new Set((products || []).map(p => (p.name || p.productName || p.title || '').trim()).filter(Boolean)));
    const fromExports = Array.from(new Set(
      (exports || []).flatMap(o => (o.items || []).map(i => (i.productName || '').trim()).filter(Boolean))
    ));
    return Array.from(new Set([...fromProducts, ...fromExports])).sort((a, b) => a.localeCompare(b, 'vi'));
  }, [products, exports]);

  /* ------------- Filter + sort ------------- */
  const filteredExports = useMemo(() => {
    const term = (searchTerm || '').toLowerCase();
    const selCustomersL = (selectedCustomers || []).map(s => s.toLowerCase().trim());
    const selProductsL = (selectedProducts || []).map(s => s.toLowerCase().trim());
    const fromObj = fromDate ? new Date(`${fromDate}T00:00:00.000`) : null;
    const toObj   = toDate   ? new Date(`${toDate}T23:59:59.999`) : null;

    const matchProduct = (doc) => {
      if (!selProductsL.length) return true;
      return (doc.items || []).some(i => selProductsL.includes((i.productName || '').toLowerCase().trim()));
    };

    const matchCustomer = (doc) => {
      if (!selCustomersL.length) return true;
      return selCustomersL.includes((doc.customerName || '').toLowerCase().trim());
    };

    const matchDate = (doc) => {
      const d = toJSDate(doc.exportDate);
      if (!d) return true;
      if (fromObj && d < fromObj) return false;
      if (toObj && d > toObj) return false;
      return true;
    };

    return (exports || []).filter(p => {
      const statusLabel = getOrderStatusChipProps(p.status).label.toLowerCase();
      const textOk =
        (p.soNumber && String(p.soNumber).toLowerCase().includes(term)) ||
        (p.customerName && p.customerName.toLowerCase().includes(term)) ||
        (p.driverName && p.driverName.toLowerCase().includes(term)) ||
        (p.vehiclePlate && p.vehiclePlate.toLowerCase().includes(term)) ||
        (p.createdBy && p.createdBy.toLowerCase().includes(term)) ||
        (p.approvedBy && p.approvedBy.toLowerCase().includes(term)) ||
        statusLabel.includes(term);

      return textOk && matchProduct(p) && matchCustomer(p) && matchDate(p);
    });
  }, [exports, searchTerm, selectedProducts, selectedCustomers, fromDate, toDate]);

  const processedExports = useMemo(
    () => stableSort(filteredExports, getComparator(order, orderBy)),
    [filteredExports, order, orderBy]
  );

  /* ------------- Filtered stats (tiền & SL theo lọc) ------------- */
  const filteredStats = useMemo(() => {
    const selProductsSet = new Set(selectedProducts || []);
    const hasProductFilter = selProductsSet.size > 0;

    const totalOrders = filteredExports.length;

    let totalQuantity = 0;
    let subtotalTotal = 0;
    let taxTotal = 0;
    let shippingTotal = 0;

    filteredExports.forEach(o => {
      const allItems = o.items || [];

      const orderSubtotalAll = allItems.reduce((s, it) =>
        s + Number(it.quantity || 0) * Number(it.exportPrice || 0), 0);

      const itemsUsed = hasProductFilter
        ? allItems.filter(it => selProductsSet.has(it.productName || ''))
        : allItems;

      const subThis = itemsUsed.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.exportPrice || 0), 0);
      const qtyThis = itemsUsed.reduce((s, it) => s + Number(it.quantity || 0), 0);

      totalQuantity += qtyThis;
      subtotalTotal += subThis;

      if (hasProductFilter) {
        const ratio = orderSubtotalAll > 0 ? (subThis / orderSubtotalAll) : 0;
        taxTotal += Number(o.taxAmount || 0) * ratio;
        shippingTotal += Number(o.shippingFee || 0) * ratio;
      } else {
        taxTotal += Number(o.taxAmount || 0);
        shippingTotal += Number(o.shippingFee || 0);
      }
    });

    const grandTotal = subtotalTotal + taxTotal + shippingTotal;
    const uniqueCustomers = new Set(filteredExports.map(o => o.customerName).filter(Boolean)).size;

    return {
      totalOrders,
      totalQuantity,
      subtotalTotal,
      taxTotal,
      shippingTotal,
      grandTotal,
      uniqueCustomers,
    };
  }, [filteredExports, selectedProducts]);

  /* ------------- UI helpers ------------- */
  const handleOpenDialog = (name, state = true) =>
    setDialogs(prev => ({ ...prev, [name]: state }));

  const handleOpenCreateDialog = () => { setCurrentExport(null); handleOpenDialog('export'); };
  const handleOpenEditDialog = (exportOrder) => { setCurrentExport(exportOrder); handleOpenDialog('export'); };

  const handleChangePage = (e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

  // Reset trang khi đổi bộ lọc
  useEffect(() => { setPage(0); }, [searchTerm, selectedCustomers, selectedProducts, fromDate, toDate, datePreset]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrderForStatusChange, setSelectedOrderForStatusChange] = useState(null);
  const statusMenuOpen = Boolean(anchorEl);
  const handleStatusClick = (e, orderRow) => { setAnchorEl(e.currentTarget); setSelectedOrderForStatusChange(orderRow); };
  const handleStatusClose = () => { setAnchorEl(null); setSelectedOrderForStatusChange(null); };

  const handleStatusUpdate = async (newStatus) => {
    if (!selectedOrderForStatusChange) return;
    try {
      await updateDoc(doc(db, 'exports', selectedOrderForStatusChange.id), { status: newStatus });
    } catch (e) {
      console.error('Error updating status:', e);
      alert('Lỗi khi cập nhật trạng thái.');
    }
    handleStatusClose();
  };

  const { roleNames = [], permissions = {} } = currentUser || {};
  const isSuperUser = roleNames.some(n => n?.toLowerCase() === 'admin') || permissions?.full_access;
  const canApprove = isSuperUser || currentUser?.permissions?.approveExports;
  const canEdit = isSuperUser || currentUser?.permissions?.editExports;
  const canDelete = isSuperUser || currentUser?.permissions?.deleteExports;

  /* ------------- Create / Update Export (ALLOW NEGATIVE STOCK + AUTO PRINT) ------------- */
  const handleSaveExport = async (exportData, action = 'saveOnly') => {
    try {
      let finalExportData = { ...exportData };

      if (currentExport) {
        await runTransaction(db, async (tx) => {
          const exportRef = doc(db, 'exports', currentExport.id);

          const deltaMap = new Map();
          (currentExport.items || []).forEach(it => {
            const q = Number(it.quantity || 0);
            deltaMap.set(it.productId, (deltaMap.get(it.productId) || 0) + q);
          });
          (exportData.items || []).forEach(it => {
            const q = Number(it.quantity || 0);
            deltaMap.set(it.productId, (deltaMap.get(it.productId) || 0) - q);
          });

          const productIds = Array.from(deltaMap.keys());
          const productRefs = productIds.map(id => doc(db, 'products', id));
          const snaps = await Promise.all(productRefs.map(r => tx.get(r)));

          const writes = productIds.map((pid, idx) => {
            const cur = Number(snaps[idx].data()?.quantity ?? 0);
            const delta = Number(deltaMap.get(pid) ?? 0);
            if (delta === 0) return null;
            const newQty = cur + delta;
            return { ref: productRefs[idx], newQty };
          }).filter(Boolean);

          writes.forEach(w => tx.update(w.ref, { quantity: w.newQty }));
          tx.update(exportRef, { ...exportData, updatedAt: serverTimestamp() });
        });

        finalExportData = { ...currentExport, ...exportData };
        alert('Cập nhật phiếu xuất thành công!');
      } else {
        const newExportRef = doc(collection(db, 'exports'));

        await runTransaction(db, async (tx) => {
          const items = exportData.items || [];
          const productRefs = items.map(it => doc(db, 'products', it.productId));
          const snaps = await Promise.all(productRefs.map(r => tx.get(r)));

          const updates = snaps.map((snap, i) => {
            const cur = Number(snap.data()?.quantity ?? 0);
            const need = Number(items[i].quantity ?? 0);
            const newQty = cur - need;
            return { ref: productRefs[i], newQty };
          });

          updates.forEach(u => tx.update(u.ref, { quantity: u.newQty }));
          tx.set(newExportRef, {
            ...exportData,
            exportDate: serverTimestamp(),
            createdBy: currentUser?.email || 'system',
          });
        });

        finalExportData = {
          ...exportData,
          id: newExportRef.id,
          exportDate: { toDate: () => new Date() },
        };
        alert('Tạo phiếu xuất thành công!');
      }

      handleOpenDialog('export', false);
      setOrderToPrint(finalExportData);
      handleOpenDialog('print', true);

      if (action === 'saveAndContract' || action === 'saveAndBoth') {
        await generateContract(finalExportData);
      }
    } catch (e) {
      console.error('Giao dịch xuất hàng thất bại:', e);
      alert(`Đã có lỗi xảy ra: ${e.message}`);
    }
  };

  /* ------------- Delete Export ------------- */
  const handleDeleteExport = async (exportOrder) => {
    if (!exportOrder) return;
    if (!window.confirm(`Bạn có chắc muốn HỦY phiếu xuất ${exportOrder.soNumber}? Hành động này sẽ HOÀN TRẢ hàng về kho.`)) return;

    try {
      await runTransaction(db, async (tx) => {
        const exportRef = doc(db, 'exports', exportOrder.id);
        const items = Array.isArray(exportOrder.items) ? exportOrder.items : [];

        const productRefs = items.map(it => doc(db, 'products', it.productId));
        const snaps = await Promise.all(productRefs.map(r => tx.get(r)));

        const updates = snaps.map((snap, i) => {
          const cur = Number(snap.data()?.quantity || 0);
          const addBack = Number(items[i]?.quantity || 0);
          return { ref: productRefs[i], newQty: cur + addBack };
        });

        updates.forEach(u => tx.update(u.ref, { quantity: u.newQty }));
        tx.delete(exportRef);
      });

      alert('Hủy phiếu xuất thành công!');
    } catch (e) {
      console.error('Lỗi hủy phiếu xuất:', e);
      alert(`Lỗi: ${e.message}`);
    }
  };

  /* ------------- Approve ------------- */
  const handleApprove = async (orderId) => {
    if (!window.confirm('Bạn có chắc muốn duyệt đơn hàng này?')) return;
    try {
      await updateDoc(doc(db, 'exports', orderId), {
        approvedBy: currentUser?.email || 'system',
        approvedAt: serverTimestamp(),
      });
      alert('Duyệt đơn hàng thành công.');
    } catch (e) {
      console.error('Lỗi khi duyệt đơn hàng:', e);
      alert('Có lỗi xảy ra khi duyệt đơn hàng.');
    }
  };

  /* ------------- Create helpers (customers, simple entities) ------------- */
  const handleSaveSimple =
    (collectionName, dialogName, existingItems, checkField = 'name') =>
    async (data) => {
      const trimmed = data[checkField]?.trim();
      if (!trimmed) return alert('Trường thông tin không được để trống.');
      const dup = existingItems.some(
        (x) => x[checkField]?.trim().toLowerCase() === trimmed.toLowerCase()
      );
      if (dup) return alert(`"${trimmed}" đã tồn tại. Vui lòng nhập giá trị khác.`);
      await addDoc(collection(db, collectionName), {
        ...data,
        [checkField]: trimmed,
        createdAt: serverTimestamp(),
      });
      handleOpenDialog(dialogName, false);
      alert('Tạo mới thành công!');
    };

  const handleSaveCustomer = async (customerData) => {
    const trimmedName = customerData.name?.trim();
    if (!trimmedName) return alert('Tên khách hàng không được để trống.');
    const dup = customers.some((c) => c.name?.trim().toLowerCase() === trimmedName.toLowerCase());
    if (dup) return alert(`Khách hàng "${trimmedName}" đã tồn tại.`);
    await addDoc(collection(db, 'customers'), {
      ...customerData,
      name: trimmedName,
      createdAt: serverTimestamp(),
    });
    handleOpenDialog('customer', false);
    alert('Tạo khách hàng mới thành công!');
  };

  /* ------------- Docx contract generation (kept) ------------- */
  const generateContract = async (orderData) => {
    try {
      const response = await fetch('/template.docx');
      if (!response.ok) throw new Error('Không tìm thấy file mẫu hợp đồng (template.docx) trong thư mục public.');
      const content = await response.arrayBuffer();
      const zip = new PizZip(content);
      const docx = new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true });

      const customerDetails = customers.find(c => c.id === orderData.customerId) || {};
      const soDate = toJSDate(orderData.soDate) || new Date();

      const data = {
        so_number: orderData.soNumber,
        so_dd: String(soDate.getDate()).padStart(2, '0'),
        so_mm: String(soDate.getMonth() + 1).padStart(2, '0'),
        so_yyyy: soDate.getFullYear(),
        company_name: 'CÔNG TY TNHH HÓA CHẤT ABC',
        company_address: '123 Đường XYZ, Phường A, Quận B, TP. HCM',
        company_phone: '0987654321',
        company_tax: '0123456789',
        customer_name: orderData.customerName,
        customer_address: customerDetails.address || '',
        customer_tax: customerDetails.code || '',
        customer_rep: customerDetails.representative || '',
        customer_position: customerDetails.position || '',
        items: (orderData.items || []).map((item, index) => ({
          stt: index + 1,
          product_name: item.productName,
          quantity: item.quantity,
          price: formatCurrency(item.exportPrice),
          total: formatCurrency(Number(item.quantity || 0) * Number(item.exportPrice || 0)),
        })),
        subtotal: formatCurrency(orderData.subtotal),
        tax: formatCurrency(orderData.taxAmount),
        shipping: formatCurrency(orderData.shippingFee),
        grand_total: formatCurrency(orderData.grandTotal),
      };

      docx.render(data);
      const out = docx.getZip().generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      saveAs(out, `HopDong_${orderData.soNumber}.docx`);
    } catch (error) {
      console.error('Lỗi khi tạo hợp đồng:', error);
      alert(`Lỗi khi tạo hợp đồng: ${error.message}`);
    }
  };

  /* ------------- Excel export (theo bộ lọc) ------------- */
  const handleExportExcel = () => {
    try {
      const selProductsSet = new Set(selectedProducts || []);
      const hasProductFilter = selProductsSet.size > 0;

      const rows = processedExports.flatMap(e =>
        (e.items || [])
          .filter(it => !hasProductFilter || selProductsSet.has(it.productName || ''))
          .map(it => ({
            'Mã đơn': e.soNumber,
            'Ngày xuất': formatDateTime(e.exportDate),
            'Khách hàng': e.customerName,
            'Tên SP': it.productName,
            'SL xuất': it.quantity,
            'Giá xuất': it.exportPrice,
            'Thành tiền': Number(it.quantity || 0) * Number(it.exportPrice || 0),
            'Tài xế': e.driverName || '',
            'Xe': e.vehiclePlate || '',
            'Người tạo': e.createdBy || '',
            'Người duyệt': e.approvedBy || 'Chưa duyệt',
            'Trạng thái': getOrderStatusChipProps(e.status).label,
          }))
      );

      if (rows.length === 0) {
        alert('Không có dữ liệu để xuất.');
        return;
      }
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, 'HangXuat');
      XLSX.writeFile(wb, `Du_Lieu_Xuat_Hang_${new Date().toLocaleDateString('vi-VN').replace(/\//g, '-')}.xlsx`);
    } catch (e) {
      console.error('Export Excel error:', e);
      alert('Không thể xuất Excel.');
    }
  };
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    alert(`Đã nhận file: ${file.name}`);
  };

  if (loading) return <Typography>Đang tải...</Typography>;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Tổng Quan & Lịch Sử Xuất Hàng</Typography>
        {(isSuperUser || currentUser?.permissions?.createExports) && (
          <Button variant="contained" onClick={handleOpenCreateDialog}>
            Tạo Phiếu Xuất
          </Button>
        )}
      </Box>

      {/* Stats */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard title="Doanh thu tháng này" value={dashboardStats.revenue.value}
                      percentageChange={dashboardStats.revenue.change}
                      icon={<MonetizationOnIcon fontSize="large" />} color="success.main" />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard title="Tổng đơn hàng tháng này" value={dashboardStats.orders.value}
                      percentageChange={dashboardStats.orders.change}
                      icon={<ReceiptIcon fontSize="large" />} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard title="Sản phẩm bán chạy" value={dashboardStats.topSellingProduct}
                      icon={<AllInboxIcon fontSize="large" />} />
          </Grid>

          <Grid item xs={12} sm={4} md={2}>
            <StatCard title="Đơn đang xử lý" value={dashboardStats.processing.value}
                      percentageChange={dashboardStats.processing.change}
                      icon={<HourglassTopIcon fontSize="large" />} color="info.main" />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <StatCard title="Đơn đã vận chuyển" value={dashboardStats.shipped.value}
                      percentageChange={dashboardStats.shipped.change}
                      icon={<LocalShippingIcon fontSize="large" />} color="success.main" />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <StatCard title="Đơn đã hủy" value={dashboardStats.cancelled.value}
                      percentageChange={dashboardStats.cancelled.change}
                      icon={<CancelIcon fontSize="large" />} color="error.main" />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <StatCard title="Đơn hàng khác" value={dashboardStats.other.value}
                      percentageChange={dashboardStats.other.change}
                      icon={<HelpOutlineIcon fontSize="large" />} color="text.secondary" />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <StatCard title="Đơn chưa thanh toán" value={dashboardStats.unpaid.value}
                      percentageChange={dashboardStats.unpaid.change}
                      icon={<PaymentIcon fontSize="large" />} color="error.main" />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <StatCard title="Đơn trả một phần" value={dashboardStats.partial.value}
                      percentageChange={dashboardStats.partial.change}
                      icon={<PaymentIcon fontSize="large" />} color="warning.main" />
          </Grid>
        </Grid>
      </Box>

      {/* Toolbar + Bộ lọc (dropdown preset) */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid
          container
          spacing={2}
          alignItems="center"
          wrap="nowrap"
          sx={{ overflowX: { xs: 'auto', md: 'visible' } }}
        >
          {/* Ô tìm kiếm giãn rộng */}
          <Grid item xs={12} md={4} sx={{ flexGrow: 1, minWidth: { md: 420, lg: 520 } }}>
            <TextField
              fullWidth size="small"
              label="Tìm theo Mã Phiếu, Tên KH, Tài xế, Xe, Người tạo/duyệt, Trạng thái..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>

          {/* Lọc theo TÊN SẢN PHẨM - MULTI */}
          <Grid item xs={12} md={3} sx={{ flexShrink: 0, minWidth: { md: 280, lg: 320 } }}>
            <Autocomplete
              multiple
              size="small"
              options={productOptions}
              value={selectedProducts}
              onChange={(_, v) => setSelectedProducts(v)}
              renderInput={(params) => <TextField {...params} label="Lọc theo TÊN SẢN PHẨM" />}
              clearOnEscape
            />
          </Grid>

          {/* Lọc theo TÊN KHÁCH HÀNG - MULTI */}
          <Grid item xs={12} md={3} sx={{ flexShrink: 0, minWidth: { md: 280, lg: 320 } }}>
            <Autocomplete
              multiple
              size="small"
              options={customerOptions}
              value={selectedCustomers}
              onChange={(_, v) => setSelectedCustomers(v)}
              renderInput={(params) => <TextField {...params} label="Lọc theo TÊN KHÁCH HÀNG" />}
              clearOnEscape
            />
          </Grid>

          {/* Dropdown preset thời gian */}
          <Grid item xs={12} md="auto" sx={{ flexShrink: 0 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="preset-label">Khoảng thời gian</InputLabel>
              <Select
                labelId="preset-label"
                value={datePreset}
                label="Khoảng thời gian"
                onChange={(e) => setDatePreset(e.target.value)}
              >
                <MenuItem value="">Tất cả</MenuItem>
                <MenuItem value="today">Hôm nay</MenuItem>
                <MenuItem value="7weeks">7 tuần qua</MenuItem>
                <MenuItem value="thisMonth">Tháng này</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Nút hành động */}
          <Grid
            item
            xs={12}
            md="auto"
            sx={{ flexShrink: 0, display: 'flex', gap: 1, alignItems: 'center' }}
          >
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportExcel} sx={{ whiteSpace: 'nowrap' }}>
              Xuất Excel
            </Button>
            <Button variant="contained" component="label" startIcon={<UploadIcon />} sx={{ whiteSpace: 'nowrap' }}>
              Import
              <input type="file" hidden accept=".xlsx, .xls" onChange={handleFileUpload} />
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setSearchTerm('');
                setSelectedCustomers([]);
                setSelectedProducts([]);
                setDatePreset('');
                setFromDate('');
                setToDate('');
              }}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Xóa lọc
            </Button>
          </Grid>
        </Grid>

        {/* Thống kê theo bộ lọc */}
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Chip color="primary" label={`Số đơn: ${filteredStats.totalOrders}`} />
            <Chip color="success" label={`Tổng SL xuất${selectedProducts.length ? ` (${selectedProducts.join(', ')})` : ''}: ${filteredStats.totalQuantity}`} />
            <Chip color="default" label={`Số KH khác nhau: ${filteredStats.uniqueCustomers}`} />
            <Chip color="info" label={`Subtotal: ${formatCurrency(filteredStats.subtotalTotal)}`} />
            <Chip color="warning" label={`Thuế: ${formatCurrency(filteredStats.taxTotal)}`} />
            <Chip color="secondary" label={`Phí VC: ${formatCurrency(filteredStats.shippingTotal)}`} />
            <Chip color="success" variant="outlined" label={`Tổng cộng: ${formatCurrency(filteredStats.grandTotal)}`} />
            {datePreset && (
              <Chip label={`Khoảng ngày: ${fromDate} → ${toDate}`} />
            )}
          </Stack>
        </Box>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Mã đơn</TableCell>
                <TableCell>Khách Hàng</TableCell>
                <TableCell>Vận chuyển</TableCell>
                <TableCell>Ngày tạo</TableCell>
                <TableCell>Người tạo</TableCell>
                <TableCell>Trạng thái ĐH</TableCell>
                <TableCell>Người duyệt</TableCell>
                <TableCell align="right">Hành động</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processedExports
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item) => {
                  const statusProps = getOrderStatusChipProps(item.status);
                  const isApproved = !!item.approvedBy;

                  return (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ fontWeight: 'bold' }}>{item.soNumber}</TableCell>
                      <TableCell>{item.customerName}</TableCell>
                      <TableCell>
                        <Typography variant="caption" component="div">TX: {item.driverName || 'N/A'}</Typography>
                        <Typography variant="caption" component="div">PX: {item.assistantDriverName || 'N/A'}</Typography>
                        <Typography variant="caption" component="div">Xe: {item.vehiclePlate || 'N/A'}</Typography>
                      </TableCell>
                      <TableCell>{formatDateTime(item.exportDate)}</TableCell>
                      <TableCell>{item.createdBy}</TableCell>
                      <TableCell>
                        <Chip
                          label={statusProps.label} color={statusProps.color} size="small"
                          onClick={(e) => handleStatusClick(e, item)} sx={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                      <TableCell>{item.approvedBy || 'Chưa duyệt'}</TableCell>
                      <TableCell align="right">
                        {/* In phiếu */}
                        <Tooltip title="In Phiếu xuất">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                setOrderToPrint(item);
                                handleOpenDialog('print', true);
                              }}
                            >
                              <PrintIcon />
                            </IconButton>
                          </span>
                        </Tooltip>

                        {/* Duyệt */}
                        {canApprove && (
                          <Tooltip title="Duyệt đơn">
                            <span>
                              <IconButton size="small" onClick={() => handleApprove(item.id)} disabled={isApproved} color="success">
                                <CheckCircleOutlineIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                        {/* Sửa */}
                        {canEdit && (
                          <Tooltip title={isApproved ? 'Đơn đã duyệt không thể sửa' : 'Chỉnh sửa'}>
                            <span>
                              <IconButton size="small" onClick={() => handleOpenEditDialog(item)} disabled={isApproved}>
                                <EditIcon color={isApproved ? 'disabled' : 'warning'} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                        {/* Xóa/Hủy */}
                        {canDelete && (
                          <Tooltip title={isApproved ? 'Đơn đã duyệt không thể hủy' : 'Hủy phiếu'}>
                            <span>
                              <IconButton size="small" onClick={() => handleDeleteExport(item)} disabled={isApproved}>
                                <DeleteIcon color={isApproved ? 'disabled' : 'error'} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={processedExports.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          labelRowsPerPage="Số dòng mỗi trang:"
        />
      </Paper>

      {/* Menu đổi trạng thái */}
      <Menu anchorEl={anchorEl} open={statusMenuOpen} onClose={handleStatusClose}>
        <MenuItem onClick={() => handleStatusUpdate('processing')}>Đang xử lý</MenuItem>
        <MenuItem onClick={() => handleStatusUpdate('shipped')}>Đã chuyển</MenuItem>
        <MenuItem onClick={() => handleStatusUpdate('cancelled')}>Hủy</MenuItem>
        <MenuItem onClick={() => handleStatusUpdate('other')}>Khác</MenuItem>
      </Menu>

      {/* Dialogs */}
      <ExportDialog
        open={dialogs.export}
        onClose={() => handleOpenDialog('export', false)}
        onSave={handleSaveExport}
        products={products}
        customers={customers}
        drivers={drivers}
        assistantDrivers={assistantDrivers}
        vehicles={vehicles}
        warehouses={warehouses}
        onAddNew={(type) => handleOpenDialog(type)}
        initialExportOrder={currentExport}
      />
      <CustomerDialog
        open={dialogs.customer}
        onClose={() => handleOpenDialog('customer', false)}
        onSave={handleSaveCustomer}
      />
      <SimpleEntityDialog
        open={dialogs.driver}
        onClose={() => handleOpenDialog('driver', false)}
        onSave={handleSaveSimple('drivers', 'driver', drivers)}
        title="Tạo Tài Xế Mới"
        label="Tên tài xế"
      />
      <SimpleEntityDialog
        open={dialogs.assistantDriver}
        onClose={() => handleOpenDialog('assistantDriver', false)}
        onSave={handleSaveSimple('assistantDrivers', 'assistantDriver', assistantDrivers)}
        title="Tạo Phụ Xe Mới"
        label="Tên phụ xe"
      />
      <SimpleEntityDialog
        open={dialogs.vehicle}
        onClose={() => handleOpenDialog('vehicle', false)}
        onSave={handleSaveSimple('vehicles', 'vehicle', vehicles)}
        title="Tạo Xe Mới"
        label="Biển số xe"
      />
      <SimpleEntityDialog
        open={dialogs.warehouse}
        onClose={() => handleOpenDialog('warehouse', false)}
        onSave={handleSaveSimple('warehouses', 'warehouse', warehouses)}
        title="Tạo Kho Mới"
        label="Tên/Địa chỉ kho"
      />

      {/* Dialog in – ép kiểu export để hiện đúng form PX */}
      <PrintDialog
        open={dialogs.print}
        onClose={() => handleOpenDialog('print', false)}
        orderData={orderToPrint}
        slipType="export"
      />
    </Box>
  );
}

export default HangXuatPage;
